import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"

const FONT_URL = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"

const T = {
  es: {
    title: "Crea tu nueva contraseña",
    subtitle: "Escríbela dos veces para confirmar que quedó bien.",
    passLabel: "Nueva contraseña",
    passPlaceholder: "Mínimo 8 caracteres",
    pass2Label: "Repite la contraseña",
    pass2Placeholder: "Escríbela de nuevo",
    button: "Guardar contraseña",
    loading: "Guardando...",
    okTitle: "Contraseña actualizada",
    okText: "Ya puedes entrar a KORIVA Studio con tu nueva contraseña.",
    okCta: "Iniciar sesión →",
    errorCorta: "La contraseña necesita al menos 8 caracteres.",
    errorDistintas: "Las dos contraseñas no coinciden.",
    errorEnlace: "Este enlace ya venció o no es válido. Pide uno nuevo.",
    errorGeneric: "Algo salió mal. Intenta de nuevo.",
    pedirOtro: "Pedir un enlace nuevo"
  },
  en: {
    title: "Create your new password",
    subtitle: "Type it twice to confirm it's correct.",
    passLabel: "New password",
    passPlaceholder: "At least 8 characters",
    pass2Label: "Repeat password",
    pass2Placeholder: "Type it again",
    button: "Save password",
    loading: "Saving...",
    okTitle: "Password updated",
    okText: "You can now sign in to KORIVA Studio with your new password.",
    okCta: "Sign in →",
    errorCorta: "Password needs at least 8 characters.",
    errorDistintas: "The two passwords don't match.",
    errorEnlace: "This link has expired or is not valid. Request a new one.",
    errorGeneric: "Something went wrong. Try again.",
    pedirOtro: "Request a new link"
  }
}

export default function KorivaNuevaClave() {
  const navigate = useNavigate()
  const [lang, setLang] = useState("es")
  const [pw, setPw] = useState("")
  const [pw2, setPw2] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [listo, setListo] = useState(false)
  const [enlaceValido, setEnlaceValido] = useState(null)

  const t = T[lang]

  // Al llegar desde el correo, Supabase abre una sesion temporal.
  // Si no hay sesion, el enlace vencio o no es valido.
  useEffect(() => {
    const comprobar = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setEnlaceValido(!!session)
    }
    const timer = setTimeout(comprobar, 900)
    return () => clearTimeout(timer)
  }, [])

  async function handleGuardar() {
    if (pw.length < 8) {
      setError(t.errorCorta)
      return
    }
    if (pw !== pw2) {
      setError(t.errorDistintas)
      return
    }
    setLoading(true)
    setError("")
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password: pw })
      if (updErr) throw updErr
      setListo(true)
    } catch (err) {
      setError(t.errorGeneric)
    } finally {
      setLoading(false)
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter") handleGuardar()
  }

  const s = {
    page: {
      minHeight: "100vh",
      background: OFF,
      fontFamily: B,
      color: TEXT,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px"
    },
    card: {
      background: WHITE,
      borderRadius: "20px",
      border: `1px solid ${BORDER}`,
      boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
      padding: "40px",
      width: "100%",
      maxWidth: "420px"
    },
    logo: {
      fontFamily: H,
      fontWeight: 800,
      fontSize: "24px",
      color: V,
      letterSpacing: "-0.02em",
      marginBottom: "8px"
    },
    title: {
      fontFamily: H,
      fontWeight: 700,
      fontSize: "26px",
      margin: "0 0 6px 0",
      letterSpacing: "-0.02em"
    },
    subtitle: {
      fontSize: "15px",
      color: MUTED,
      margin: "0 0 28px 0",
      lineHeight: 1.6
    },
    label: {
      display: "block",
      fontFamily: H,
      fontWeight: 600,
      fontSize: "14px",
      marginBottom: "8px"
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: "13px 16px",
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      fontSize: "15px",
      fontFamily: B,
      outline: "none",
      marginBottom: "18px",
      background: WHITE
    },
    button: {
      width: "100%",
      padding: "14px",
      borderRadius: "12px",
      border: "none",
      background: V,
      color: WHITE,
      fontFamily: H,
      fontWeight: 700,
      fontSize: "16px",
      cursor: loading ? "default" : "pointer",
      opacity: loading ? 0.7 : 1,
      marginTop: "6px"
    },
    error: {
      background: "#FEF2F2",
      color: "#DC2626",
      borderRadius: "10px",
      padding: "12px 14px",
      fontSize: "14px",
      marginBottom: "18px",
      border: "1px solid #FECACA"
    },
    okIcon: {
      width: 56,
      height: 56,
      background: V_SOFT,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "20px",
      fontSize: 22,
      color: V
    },
    footer: {
      textAlign: "center",
      fontSize: "14px",
      color: MUTED,
      marginTop: "22px"
    },
    link: {
      color: V,
      fontWeight: 600,
      cursor: "pointer",
      textDecoration: "none"
    },
    langSwitch: {
      display: "flex",
      gap: "6px",
      justifyContent: "flex-end",
      marginBottom: "20px"
    },
    langBtn: (active) => ({
      padding: "5px 12px",
      borderRadius: "8px",
      border: `1px solid ${active ? V : BORDER}`,
      background: active ? V_SOFT : WHITE,
      color: active ? V : MUTED,
      fontFamily: H,
      fontWeight: 600,
      fontSize: "13px",
      cursor: "pointer"
    }),
    ojo: {
      position: "absolute",
      right: 14,
      top: "22px",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      color: MUTED,
      display: "flex",
      alignItems: "center"
    }
  }

  const iconoOjo = showPw
    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>

  return (
    <div style={s.page}>
      <link rel="stylesheet" href={FONT_URL} />
      <div style={s.card}>
        <div style={s.langSwitch}>
          <button style={s.langBtn(lang === "es")} onClick={() => setLang("es")}>ES</button>
          <button style={s.langBtn(lang === "en")} onClick={() => setLang("en")}>EN</button>
        </div>
        <div style={s.logo}>KORIVA Studio</div>

        {listo ? (
          <>
            <div style={s.okIcon}>✓</div>
            <h1 style={s.title}>{t.okTitle}</h1>
            <p style={s.subtitle}>{t.okText}</p>
            <button style={s.button} onClick={() => navigate("/login")}>{t.okCta}</button>
          </>
        ) : enlaceValido === false ? (
          <>
            <h1 style={s.title}>{t.title}</h1>
            <div style={s.error}>{t.errorEnlace}</div>
            <button style={s.button} onClick={() => navigate("/recuperar")}>{t.pedirOtro}</button>
          </>
        ) : (
          <>
            <h1 style={s.title}>{t.title}</h1>
            <p style={s.subtitle}>{t.subtitle}</p>

            {error && <div style={s.error}>{error}</div>}

            <label style={s.label}>{t.passLabel}</label>
            <div style={{ position: "relative", width: "100%" }}>
              <input
                style={{ ...s.input, paddingRight: 44 }}
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t.passPlaceholder}
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Ocultar" : "Mostrar"} style={s.ojo}>
                {iconoOjo}
              </button>
            </div>

            <label style={s.label}>{t.pass2Label}</label>
            <input
              style={s.input}
              type={showPw ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t.pass2Placeholder}
              autoComplete="new-password"
            />

            <button style={s.button} onClick={handleGuardar} disabled={loading}>
              {loading ? t.loading : t.button}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
